import Bugsnag from '@bugsnag/js'
import BugsnagPluginVue from '@bugsnag/plugin-vue'
import BugsnagPerformance from '@bugsnag/browser-performance'

const API_KEY = 'e59390fc1d73d0e691454a6b6ce00f28'

// Vite emits these when a client running an old index.html requests hashed
// chunks that no longer exist after a deploy. They are benign (the app retries
// / the user reloads) but extremely high-volume, and were the single largest
// drain on our Bugsnag event quota. Discard them rather than reporting.
const CHUNK_LOAD_ERROR = /Unable to preload CSS for|Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module|dynamically imported module/i

function isChunkLoadError(event) {
  return event.errors.some((error) =>
    CHUNK_LOAD_ERROR.test(error.errorMessage || '')
  )
}

// Third-party ad/analytics scripts (Google Publisher Tag, DoubleClick,
// AdSense, GA, Funding Choices) constantly fetch their own endpoints, which
// fail due to ad blockers and flaky networks. Because Bugsnag instruments
// window.fetch globally, those rejections surface as unhandled
// "Failed to fetch" errors with no app code in the stack — not actionable for
// us. Discard a network fetch error only when a stack frame belongs to one of
// these origins, so genuine app fetch failures are still reported.
const NETWORK_FETCH_ERROR = /Failed to fetch|Load failed|NetworkError when attempting to fetch/i
const THIRD_PARTY_NOISE = /googletagservices\.com|doubleclick\.net|googlesyndication\.com|google-analytics\.com|googletagmanager\.com|fundingchoicesmessages\.google\.com|adtrafficquality\.google/i

function isThirdPartyFetchError(event) {
  return event.errors.some((error) => {
    if (!NETWORK_FETCH_ERROR.test(error.errorMessage || '')) return false
    return (error.stacktrace || []).some((frame) =>
      THIRD_PARTY_NOISE.test(frame.file || '')
    )
  })
}

// When a chunk fails to load it almost always means the client is running a
// stale index.html after a deploy; a reload fetches the new asset manifest and
// fixes it. Vite fires `vite:preloadError` on window for these. Reload once,
// debounced via sessionStorage so a genuinely offline client can't loop.
const RELOAD_KEY = 'jwx:lastChunkReload'
const RELOAD_DEBOUNCE_MS = 10000

function reloadOnChunkError() {
  window.addEventListener('vite:preloadError', (event) => {
    // We're handling it — stop Vite from rethrowing (which would also hit Bugsnag).
    event.preventDefault()

    let last = 0
    try {
      last = Number(window.sessionStorage.getItem(RELOAD_KEY)) || 0
    } catch (e) {
      // sessionStorage can throw (private mode / sandboxed iframe); just reload.
    }

    if (Date.now() - last < RELOAD_DEBOUNCE_MS) return

    try {
      window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
    } catch (e) {
      // ignore — worst case we reload again next time
    }

    window.location.reload()
  })
}

/**
 * Start Bugsnag + performance monitoring for a page entry point.
 *
 * @param {string} context - Bugsnag `event.context` identifying the page,
 *   e.g. 'Lifestyle Web - Map'.
 * @returns the Bugsnag Vue plugin to register via `app.use(...)`.
 */
export default function initBugsnag(context) {
  reloadOnChunkError()

  Bugsnag.start({
    apiKey: API_KEY,
    releaseStage: import.meta.env.MODE,
    plugins: [new BugsnagPluginVue()],
    onError: function (event) {
      if (isChunkLoadError(event)) return false
      if (isThirdPartyFetchError(event)) return false
      event.context = context
    },
  })

  BugsnagPerformance.start({ apiKey: API_KEY })

  return Bugsnag.getPlugin('vue')
}
